import { EditorContent, useEditor } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import History from '@tiptap/extension-history';
import { cn } from '@/lib/cn';

/**
 * Rich-text-LITE editor (W7) under the BINDING CONDITIONS approved
 * 2026-08-07: dispatcher-only dependency; the schema is built from
 * individual extensions so it CANNOT produce anything beyond
 * doc/paragraph/text/bulletList/orderedList/listItem nodes and
 * bold/italic marks — no headings, no hardBreak (Shift+Enter is a
 * plain paragraph split), no pasted styling survives. The server's zod
 * whitelist rejects unknown node/mark types at write time regardless;
 * this editor simply never generates them. Content travels as
 * ProseMirror JSON; the portal renders it with our own JSON walker.
 */

export const EMPTY_DOC = {
  type: 'doc',
  content: [{ type: 'paragraph', content: [] }],
};

const EXTENSIONS = [
  Document,
  Paragraph,
  Text,
  Bold,
  Italic,
  BulletList,
  OrderedList,
  ListItem,
  History,
];

function ToolbarButton({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onMouseDown={(e) => {
        e.preventDefault(); // keep editor focus
        onClick();
      }}
      className={cn(
        'rounded px-2 py-1 text-xs font-semibold',
        active
          ? 'bg-breaker-ink text-white'
          : 'text-breaker-ink-2 hover:bg-breaker-ground-2',
      )}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({
  doc,
  onChange,
}: {
  doc: unknown;
  onChange: (doc: unknown) => void;
}) {
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: (doc as object) ?? EMPTY_DOC,
    onUpdate: ({ editor: e }) => onChange(e.getJSON()),
    editorProps: {
      attributes: {
        class:
          'rich-text-edit min-h-[96px] px-3 py-2 text-sm text-breaker-ink focus:outline-none',
      },
    },
  });

  if (!editor) return null;

  return (
    <div className="rounded-md border border-breaker-line-2 bg-white">
      <div className="flex gap-1 border-b border-breaker-line px-2 py-1">
        <ToolbarButton
          label="Bold"
          active={editor.isActive('bold')}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </ToolbarButton>
        <ToolbarButton
          label="Italic"
          active={editor.isActive('italic')}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          label="Bullet list"
          active={editor.isActive('bulletList')}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • List
        </ToolbarButton>
        <ToolbarButton
          label="Numbered list"
          active={editor.isActive('orderedList')}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1. List
        </ToolbarButton>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
